import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Archive, Check, Loader2, Send, X } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import { useConfirm } from '@orisini/ui/components/confirm'
import { cn } from '@orisini/ui/lib/utils'
import { accountsApi, type Workspace } from '../../accounts/lib/accounts'
import { ContentList } from '../components/ContentList'
import { contentApi, type ContentItem } from '../lib/content'

type Tab = 'drafts' | 'publish'

/**
 * /review — semua keputusan yang menunggu user, di satu tempat:
 * - Drafts: draft dari akun ber-mode Approval Required;
 * - Ready to publish: konten jadi dari akun yang "Ask before publishing".
 */
export default function ReviewPage() {
  const confirm = useConfirm()
  const [params, setParams] = useSearchParams()
  const tab: Tab = params.get('tab') === 'publish' ? 'publish' : 'drafts'
  const account = params.get('account') ?? ''
  const [items, setItems] = useState<ContentItem[] | null>(null)
  const [counts, setCounts] = useState({ drafts: 0, publish: 0 })
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState<string | null>(null)

  const muat = useCallback(async () => {
    try {
      const r = await contentApi.review(tab, account || undefined)
      setItems(r.items)
      setCounts(r.reviewCounts)
      setPicked(new Set())
    } catch (err) {
      setItems([])
      toast.error(err instanceof Error ? err.message : "Couldn't load Review")
    }
  }, [tab, account])

  useEffect(() => {
    setItems(null)
    void muat()
  }, [muat])
  useEffect(() => {
    accountsApi.workspaces().then(setWorkspaces).catch(() => undefined)
  }, [])
  // Draft ditulis di latar; segarkan berkala supaya yang baru jadi muncul sendiri.
  useEffect(() => {
    const t = setInterval(() => void muat(), 8000)
    return () => clearInterval(t)
  }, [muat])

  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params)
    if (v) next.set(k, v)
    else next.delete(k)
    setParams(next, { replace: true })
  }

  const satu = async (it: ContentItem, action: 'approve' | 'reject' | 'queue' | 'publish' | 'keep') => {
    if (action === 'publish') {
      const ok = await confirm({ title: 'Post this now?', description: `It goes to ${it.workspaceName} on ${it.platform} as soon as Orisini Agent picks it up.`, confirmLabel: 'Post now' })
      if (!ok) return
    }
    setBusy(`${action}:${it.id}`)
    try {
      await contentApi.act(it.id, action)
      toast.success(
        { approve: 'Approved — producing now', reject: 'Rejected', queue: 'Added to the queue', publish: 'Sent to post now', keep: 'Kept in the Content Bank' }[action],
      )
      await muat()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong')
      await muat()
    } finally {
      setBusy(null)
    }
  }

  const massal = async (action: 'approve' | 'reject') => {
    setBusy(`bulk:${action}`)
    try {
      const r = await contentApi.bulk([...picked], action)
      toast.success(action === 'approve' ? `${r.updated} approved — producing now` : `${r.updated} rejected`)
      await muat()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Review</h1>
          <p className="text-sm text-muted-foreground">Drafts waiting for your approval, and finished posts waiting for permission to publish.</p>
        </div>
        {workspaces.length > 1 && (
          <select aria-label="Filter by project" className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={account} onChange={(e) => set('account', e.target.value)}>
            <option value="">All projects</option>
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        )}
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 rounded-md bg-muted p-1">
          {(
            [
              ['drafts', 'Drafts', counts.drafts],
              ['publish', 'Ready to publish', counts.publish],
            ] as const
          ).map(([k, label, n]) => (
            <button key={k} onClick={() => set('tab', k === 'drafts' ? '' : k)} className={cn('rounded px-3 py-1.5 text-sm', tab === k ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground')}>
              {label} <span className="tabular-nums text-muted-foreground">{n}</span>
            </button>
          ))}
        </div>
        {tab === 'drafts' && picked.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{picked.size} chosen</span>
            <Button size="sm" disabled={!!busy} onClick={() => massal('approve')}>
              {busy === 'bulk:approve' ? <Loader2 className="animate-spin" /> : <Check />} Approve
            </Button>
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => massal('reject')}>
              <X /> Reject
            </Button>
          </div>
        )}
      </div>

      {items === null ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {tab === 'drafts' ? (
            <>
              Nothing to review. Pick stories in <Link to="/discover" className="text-foreground underline-offset-4 hover:underline">Discover</Link> or start one in{' '}
              <Link to="/create" className="text-foreground underline-offset-4 hover:underline">Create</Link>.
            </>
          ) : (
            'Nothing is waiting for permission to publish.'
          )}
        </p>
      ) : (
        <ContentList
          items={items}
          picked={tab === 'drafts' ? picked : undefined}
          onToggle={
            tab === 'drafts'
              ? (id) => {
                  const next = new Set(picked)
                  if (next.has(id)) next.delete(id)
                  else next.add(id)
                  setPicked(next)
                }
              : undefined
          }
          actions={(it) =>
            tab === 'drafts' ? (
              <>
                <Button size="sm" asChild variant="outline">
                  <Link to={`/content/${it.id}`}>Open</Link>
                </Button>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => satu(it, 'approve')} aria-label="Approve" title="Approve">
                    {busy === `approve:${it.id}` ? <Loader2 className="animate-spin" /> : <Check />}
                  </Button>
                  <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => satu(it, 'reject')} aria-label="Reject" title="Reject">
                    <X />
                  </Button>
                </div>
              </>
            ) : (
              <>
                {it.productCode && (
                  <Button size="sm" disabled={!!busy} onClick={() => satu(it, it.destination === 'publish' ? 'publish' : 'queue')}>
                    {busy?.endsWith(it.id) ? <Loader2 className="animate-spin" /> : <Send />}
                    {it.destination === 'publish' ? 'Post now' : 'Add to queue'}
                  </Button>
                )}
                <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => satu(it, 'keep')}>
                  <Archive /> Keep in Bank
                </Button>
              </>
            )
          }
        />
      )}
    </div>
  )
}
